// AI解析結果（scans/{scanId}.analysis）→ 求人票の編集用データ（draft）への変換。
//
// draft は recruits/{companyId}_2027（+ jobPostings/{year}）と同じ document 構造。
// 型・デフォルト値は src/type.ts を唯一の正として再利用し、ここでは
// 「スキャン結果を各フィールドに当てはめる」ことだけを行う。
// 求人票にはあるが求人データ構造に専用フィールドが無い項目（応募資格・研修・
// 最寄り駅など）は、内容を失わないよう *.otherText 等に見出し付きで格納する。

import {
  defaultSummaryData, defaultInfo, defaultTarget,
  defaultEmployment20, defaultEmployment18, defaultEmployment17,
  defaultBenefit, defaultApplicationInfo, defaultHashTagsData,
  summaryData, infoData, targetData,
  employmentData20, employmentData18, employmentData17,
  benefitData, applicationInfoData, hashTagsData,
} from 'src/type';

export const LOW_CONFIDENCE = 0.75;

export type ScanDraft = {
  summary: summaryData;
  info: infoData;
  target: targetData;
  employment20: employmentData20;
  employment18: employmentData18;
  employment17: employmentData17;
  benefit: benefitData;
  applicationInfo: applicationInfoData;
  hashTag: hashTagsData;
};

type Leaf = { value: unknown; rawText?: string | null; confidence?: number } | null | undefined;
type Analysis = Record<string, any> | null | undefined;

// ---- 取り出しヘルパー ----------------------------------------------------------
const str = (leaf: Leaf): string =>
  leaf && leaf.value != null && String(leaf.value).trim() !== '' ? String(leaf.value).trim() : '';

const digits = (v: unknown): string => {
  if (v == null) return '';
  const m = String(v).replace(/[０-９]/g, (d) => String('０１２３４５６７８９'.indexOf(d))).match(/\d[\d,]*/);
  return m ? m[0].replace(/,/g, '') : '';
};

const arr = (leaf: unknown): Leaf[] => (Array.isArray(leaf) ? (leaf as Leaf[]) : []);
const arrVals = (leaf: unknown): string[] => arr(leaf).map(str).filter(Boolean);
const conf = (leaf: Leaf): number | undefined =>
  leaf && typeof leaf.confidence === 'number' ? leaf.confidence : undefined;
const arrConf = (leaf: unknown): number | undefined => {
  const cs = arr(leaf).map(conf).filter((c): c is number => typeof c === 'number');
  return cs.length ? Math.min(...cs) : undefined;
};

const section = (label: string, body: string): string =>
  body && body.trim() ? `【${label}】\n${body.trim()}` : '';
const joinSections = (...parts: string[]): string => parts.filter(Boolean).join('\n\n');

const parseTimeRange = (s: string): { start: string; end: string } | null => {
  const m = s.match(/(\d{1,2})\s*[:：]\s*(\d{2})\D{0,4}[〜~\-–ー]\D{0,4}(\d{1,2})\s*[:：]\s*(\d{2})/);
  if (!m) return null;
  const pad = (h: string, mm: string) => `${h.padStart(2, '0')}:${mm}`;
  return { start: pad(m[1], m[2]), end: pad(m[3], m[4]) };
};

const parseBreakMinutes = (s: string): string => {
  const hm = s.match(/(\d+(?:\.\d+)?)\s*時間/);
  if (hm) return String(Math.round(parseFloat(hm[1]) * 60));
  const mm = s.match(/(\d+)\s*分/);
  return mm ? mm[1] : '';
};

// ---- analysis → draft --------------------------------------------------------
export function analysisToDraft(analysis: Analysis): ScanDraft {
  const a: Record<string, any> = analysis || {};
  const salary = a.salary || {};

  const companyName = str(a.companyName);
  const salonName = str(a.salonName);
  const jobDescription = str(a.jobDescription);
  const workingHours = str(a.workingHours);
  const holidays = str(a.holidays);
  const holidayPolicy = str(a.holidayPolicy);
  const socialInsurance = str(a.socialInsurance);
  const trialPeriod = str(a.trialPeriod);
  const training = str(a.training);
  const applicationRequirements = str(a.applicationRequirements);
  const remarks = str(a.remarks);
  const locations = arrVals(a.locations);
  const stations = arrVals(a.nearestStations);
  const benefits = arrVals(a.benefits);
  const allowances = arrVals(a.allowances);
  const selectionFlow = arrVals(a.selectionFlow);

  const insuranceList = socialInsurance
    ? socialInsurance.split(/[、,／/・\s]+/).map((x) => x.trim()).filter(Boolean)
    : [];
  const commuteItem = allowances.find((x) => /通勤|交通費/.test(x)) || '';

  const wh = parseTimeRange(workingHours);

  const summary: summaryData = {
    ...defaultSummaryData,
    name: salonName || companyName,
    companyPr: joinSections(section('仕事内容', jobDescription), section('備考', remarks)),
  };

  const info: infoData = {
    ...defaultInfo,
    companyName,
    address: locations.join(' / '),
    phoneNumber: str(a.contactPhone),
    email: str(a.contactEmail),
    homepageUrl: str(a.companyUrl),
    instagram: str(a.instagram),
    establishedDate: str(a.establishedDate),
    numberOfEmployees: str(a.employeeCount),
    recruiterName: str(a.contactName),
  };

  const employment20: employmentData20 = {
    ...defaultEmployment20,
    type: str(a.employmentType),
    contents: jobDescription,
    baseSalary: digits(salary.baseSalary),
    totalSalary: digits(salary.monthlyMax ?? salary.monthlyMin),
    allowance: allowances.join('、'),
    benefits: benefits.join('、'),
    insurance: insuranceList,
    employmentTrial: {
      ...defaultEmployment20.employmentTrial,
      exist: trialPeriod ? true : null,
      text: trialPeriod,
    },
    fixedOvertime: {
      ...defaultEmployment20.fixedOvertime,
      exist: salary.fixedOvertime ? true : null,
      price: digits(salary.fixedOvertime),
    },
    otherText: joinSections(
      section('給与（原文）', String(salary.rawText || '').trim()),
      section('研修・教育', training),
      section('最寄り駅', stations.join('、')),
    ),
  };

  const benefit: benefitData = {
    ...defaultBenefit,
    workingHours: {
      ...defaultBenefit.workingHours,
      time1Start: wh ? wh.start : '',
      time1End: wh ? wh.end : '',
    },
    dailyBreak: parseBreakMinutes(str(a.breakTime)),
    holidayPolicy: { exist: null, text: holidayPolicy },
    annualHolidays: digits(a.annualHolidays && a.annualHolidays.value),
    insurance: insuranceList,
    commutingAllowance: {
      exist: commuteItem ? 'あり' : null,
      text: commuteItem,
    },
    otherText: joinSections(
      section('休日・休暇', holidays),
      wh ? '' : section('勤務時間（原文）', workingHours),
    ),
  };

  const applicationInfo: applicationInfoData = {
    ...defaultApplicationInfo,
    recruitsBeauty: digits(str(a.recruitCount)),
    selectionRound1: selectionFlow[0] || '',
    selectionRound2: selectionFlow[1] || '',
    selectionRound3: selectionFlow[2] || '',
    selectionOtherDetails: joinSections(
      section('応募資格', applicationRequirements),
      selectionFlow.length > 3 ? section('選考フロー（続き）', selectionFlow.slice(3).join(' → ')) : '',
    ),
  };

  return {
    summary,
    info,
    target: { ...defaultTarget },
    employment20,
    employment18: { ...defaultEmployment18 },
    employment17: { ...defaultEmployment17 },
    benefit,
    applicationInfo,
    hashTag: { ...defaultHashTagsData },
  };
}

// ---- confidence マップ（確認画面の「要確認」表示用）---------------------------
export function buildConfidence(analysis: Analysis): Record<string, number> {
  const a: Record<string, any> = analysis || {};
  const out: Record<string, number> = {};
  const set = (path: string, c: number | undefined) => {
    if (typeof c === 'number') out[path] = c;
  };

  set('info.companyName', conf(a.companyName));
  set('summary.name', conf(a.salonName) ?? conf(a.companyName));
  set('employment20.contents', conf(a.jobDescription));
  set('employment20.type', conf(a.employmentType));
  set('salary', a.salary && typeof a.salary.confidence === 'number' ? a.salary.confidence : undefined);
  set('benefit.workingHours', conf(a.workingHours));
  set('benefit.dailyBreak', conf(a.breakTime));
  set('benefit.otherText', conf(a.holidays));
  set('benefit.holidayPolicy', conf(a.holidayPolicy));
  set('benefit.annualHolidays', conf(a.annualHolidays));
  set('info.address', arrConf(a.locations));
  set('employment20.insurance', conf(a.socialInsurance));
  set('employment20.benefits', arrConf(a.benefits));
  set('employment20.allowance', arrConf(a.allowances));
  set('employment20.employmentTrial', conf(a.trialPeriod));
  set('applicationInfo.selection', arrConf(a.selectionFlow));
  set('applicationInfo.selectionOtherDetails', conf(a.applicationRequirements));
  set('info.phoneNumber', conf(a.contactPhone));
  set('info.email', conf(a.contactEmail));
  set('info.recruiterName', conf(a.contactName));
  set('info.homepageUrl', conf(a.companyUrl));
  set('info.establishedDate', conf(a.establishedDate));
  set('info.numberOfEmployees', conf(a.employeeCount));

  return out;
}

export function needsReview(confidence: Record<string, number> | undefined, path: string): boolean {
  return !!confidence && typeof confidence[path] === 'number' && confidence[path] < LOW_CONFIDENCE;
}
